import { createApi } from '@reduxjs/toolkit/query/react';
import dynamicBaseQueryWithLogger from '@/services/utils/dynamicBaseQueryWithLogger';
import { handlerErrorResponse, handleUnwrapRTKError } from '@/services/utils/handlers';
import { setAppNotice } from '@/app/store/appShellSlice';

const BASE_URL = process.env.NEXT_PUBLIC_REPAROLY_API_URL ?? 'https://api.reparoly.local';

export interface ApiResponseBase<T> {
  success: boolean;
  message: string;
  data: T;
}

export type RegisterClientInput = {
  name: string;
  phone_number: string;
  contact_number?: string | null;
};

export type ClientData = {
  id: number;
  company_id?: number;
  name: string;
  phone_number: string;
  contact_number?: string | null;
  created_at?: string;
  updated_at?: string;
};

export const clientsApi = createApi({
  reducerPath: 'clientsApi',
  baseQuery: dynamicBaseQueryWithLogger(BASE_URL),
  endpoints: (builder) => ({
    // GET /v1/clients?query=&page=&page_size=
    getClientsList: builder.query<
      ApiResponseBase<{ total: number; page: number; page_size: number; items: ClientData[] }>,
      { query?: string; page?: number; page_size?: number }
    >({
      query: ({ query = '', page = 1, page_size = 20 } = {}) => ({
        url:
          `/v1/clients` +
          `?query=${encodeURIComponent(query)}` +
          `&page=${encodeURIComponent(String(page))}` +
          `&page_size=${encodeURIComponent(String(page_size))}`,
        method: 'GET',
      }),
      transformErrorResponse: handlerErrorResponse('Clients.List'),
    }),
    // POST /v1/clients/register
    registerClient: builder.mutation<ApiResponseBase<ClientData>, RegisterClientInput>({
      query: (body) => ({ url: '/v1/clients', method: 'POST', body }),
      transformErrorResponse: handlerErrorResponse('Clients.Register'),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al registrar el cliente',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Clients.Register'}${e?.code ? ` — Codigo: ${e.code}` : ''}`,
              messageFormatted: '',
              action: 'clients.register',
              endpointName: e?.endpointName || 'Clients.Register',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),
    // PUT /v1/clients/{id}
    updateClient: builder.mutation<
      ApiResponseBase<{ id: number }> | ApiResponseBase<unknown>,
      { id: number | string; body: { name: string; phone_number?: string; contact_number?: string } }
    >({
      query: ({ id, body }) => ({ url: `/v1/clients/${id}`, method: 'PUT', body }),
      transformErrorResponse: handlerErrorResponse('Clients.Update'),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al actualizar el cliente',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Clients.Update'}${e?.code ? ` — Codigo: ${e.code}` : ''}`,
              messageFormatted: '',
              action: 'clients.update',
              endpointName: e?.endpointName || 'Clients.Update',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),
  }),
});

export const { useRegisterClientMutation, useGetClientsListQuery, useLazyGetClientsListQuery, useUpdateClientMutation } =
  clientsApi;
